import { createBrowserRouter, Navigate, redirect } from "react-router";
import { AppLayout } from "./components/AppLayout";
import { RequireAuth } from "./components/RequireAuth";
import { SecurityPage } from "./pages/SecurityPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { LimitsPage } from "./pages/LimitsPage";
import { AccountLayout } from "./pages/account/AccountLayout";
import { AppsPage } from "./pages/AppsPage";
import { BillingDetailsPage } from "./pages/BillingDetailsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { BillingPage } from "./pages/BillingPage";
import { TunnelsPage } from "./pages/TunnelsPage";
import { NewTunnelPage } from "./pages/NewTunnelPage";
import { TunnelDetailPage } from "./pages/tunnel/TunnelDetailPage";
import { DomainsPage } from "./pages/DomainsPage";
import { NewDomainPage } from "./pages/NewDomainPage";
import { DomainDetailPage } from "./pages/domain/DomainDetailPage";
import { DomainDnsTab, DomainOrdersTab, DomainOverviewTab, DomainSettingsTab } from "./pages/domain/tabs";
import { DatabasesPage } from "./pages/DatabasesPage";
import { SchedulesPage } from "./pages/SchedulesPage";
import { NewSchedulePage } from "./pages/NewSchedulePage";
import { ScheduleDetailPage } from "./pages/schedule/ScheduleDetailPage";
import { SchedulesTab } from "./pages/app/SchedulesTab";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { LoginPage } from "./pages/LoginPage";
import { OAuthAuthorizePage } from "./pages/OAuthAuthorizePage";
import { OAuthSigninPage } from "./pages/OAuthSigninPage";
import { NewAppPage } from "./pages/NewAppPage";
import { NewDatabasePage } from "./pages/NewDatabasePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { TeamPage } from "./pages/TeamPage";
import { SupportPage } from "./pages/SupportPage";
import { ReferralsPage } from "./pages/ReferralsPage";
import { AFFILIATE_URL } from "./lib/affiliate";
import { NewTicketPage } from "./pages/support/NewTicketPage";
import { TicketDetailPage } from "./pages/support/TicketDetailPage";
import { ActivityTab as AiActivityTab } from "./pages/ai/ActivityTab";
import { AiLayout } from "./pages/ai/AiLayout";
import { ModelsTab as AiModelsTab } from "./pages/ai/ModelsTab";
import { OverviewTab as AiOverviewTab } from "./pages/ai/OverviewTab";
import { AppDetailPage } from "./pages/app/AppDetailPage";
import { DatabaseAccessTab } from "./pages/database/AccessTab";
import { DatabaseBackupsTab } from "./pages/database/BackupsTab";
import { DatabaseDetailPage } from "./pages/database/DatabaseDetailPage";
import { DatabaseLogsTab } from "./pages/database/LogsTab";
import { DatabaseOverviewTab } from "./pages/database/OverviewTab";
import { ActivityTab } from "./pages/app/ActivityTab";
import { DeploymentsTab } from "./pages/app/DeploymentsTab";
import { DomainsTab } from "./pages/app/DomainsTab";
import { EnvTab } from "./pages/app/EnvTab";
import { LogsTab } from "./pages/app/LogsTab";
import { OverviewTab } from "./pages/app/OverviewTab";
import { SettingsTab } from "./pages/app/SettingsTab";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  /* Where a GitHub or GitLab sign-in lands, via the API, with a one-time code. */
  { path: "/oauth/signin", element: <OAuthSigninPage /> },

  /*
   * The consent screen, behind the same guard as everything else: somebody
   * arriving without a session signs in first and comes back here with the
   * query string intact, which is what `RequireAuth` preserves it for.
   *
   * Outside `AppLayout` on purpose. This page is not part of the dashboard a
   * customer is using — it is a question asked on behalf of another service,
   * and framing it in the app's own navigation invites somebody to wander off
   * mid-flow into a half-finished authorization.
   */
  {
    path: "/oauth/authorize",
    element: (
      <RequireAuth>
        <OAuthAuthorizePage />
      </RequireAuth>
    ),
  },
  { path: "/register", element: <RegisterPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // Anonymous: the token in the link is the whole credential, and requiring a
  // session first would ask for the sign-in this page exists to unlock.
  { path: "/verify-email", element: <VerifyEmailPage /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/apps" replace /> },

      // What you build with.
      { path: "apps", element: <AppsPage /> },
      { path: "apps/new", element: <NewAppPage /> },
      {
        path: "apps/:appId",
        element: <AppDetailPage />,
        children: [
          { index: true, element: <OverviewTab /> },
          {
            /*
             * Releases and runtime logs under one tab, with a sub-nav --
             * `pages/app/ActivityTab.tsx` says why. The old `/logs` keeps
             * working: it is the URL people paste into a chat, and a nav
             * tidy-up is not worth breaking one.
             */
            path: "deployments",
            element: <ActivityTab />,
            children: [
              { index: true, element: <DeploymentsTab /> },
              { path: "logs", element: <LogsTab /> },
            ],
          },
          {
            /*
             * A loader rather than a `<Navigate>` element: the data router
             * resolves this before anything renders, so the old URL never
             * mounts a component to immediately unmount it -- and a redirect
             * that happens outside render is one a test can follow.
             */
            path: "logs",
            loader: ({ params }) => redirect(`/apps/${params.appId}/deployments/logs`),
          },
          { path: "env", element: <EnvTab /> },
          { path: "domains", element: <DomainsTab /> },
          { path: "schedules", element: <SchedulesTab /> },
          { path: "settings", element: <SettingsTab /> },
        ],
      },
      { path: "schedules", element: <SchedulesPage /> },
      { path: "schedules/new", element: <NewSchedulePage /> },
      { path: "schedules/:scheduleId", element: <ScheduleDetailPage /> },
      { path: "tunnels", element: <TunnelsPage /> },
      { path: "tunnels/new", element: <NewTunnelPage /> },
      { path: "tunnels/:tunnelId", element: <TunnelDetailPage /> },
      { path: "domains", element: <DomainsPage /> },
      { path: "domains/new", element: <NewDomainPage /> },
      {
        path: "domains/:domainId",
        element: <DomainDetailPage />,
        children: [
          { index: true, element: <DomainOverviewTab /> },
          { path: "dns", element: <DomainDnsTab /> },
          { path: "settings", element: <DomainSettingsTab /> },
          { path: "orders", element: <DomainOrdersTab /> },
        ],
      },
      { path: "databases", element: <DatabasesPage /> },
      { path: "databases/new", element: <NewDatabasePage /> },

      /*
       * AI inference, as a section rather than one page.
       *
       * It was one page, and the note here said why: the catalogue was short and
       * the usage table was empty until somebody had made a call, so splitting
       * them meant two half-screens. Both halves of that stopped being true — the
       * daily catalogue sync discovers models an operator can list without a
       * deploy, and usage grew a range, a per-key breakdown, filters and an
       * export.
       *
       * Routed like the app and database tabs, so each is a URL that survives a
       * reload and can be pasted into a support reply.
       */
      {
        path: "ai",
        element: <AiLayout />,
        children: [
          { index: true, element: <AiOverviewTab /> },
          { path: "models", element: <AiModelsTab /> },
          { path: "activity", element: <AiActivityTab /> },
        ],
      },
      /*
       * The same shape apps have. Logs, backups and access used to be modals on
       * the list, which meant none of them were linkable or survived a refresh.
       */
      {
        path: "databases/:databaseId",
        element: <DatabaseDetailPage />,
        children: [
          { index: true, element: <DatabaseOverviewTab /> },
          { path: "logs", element: <DatabaseLogsTab /> },
          { path: "backups", element: <DatabaseBackupsTab /> },
          { path: "access", element: <DatabaseAccessTab /> },
        ],
      },

      // Only where the deployment runs a programme, like its row in the rail.
      ...(AFFILIATE_URL ? [{ path: "referrals", element: <ReferralsPage /> }] : []),

      /*
       * Support, at the top level rather than under /settings.
       *
       * A ticket is not a setting, and the URL is one somebody pastes to a
       * colleague — `/support/MX-…` reads as what it is. Static before dynamic
       * so `new` cannot be read as a ticket id; the ranker would get this right
       * anyway, and it is written in this order so a reader does not have to
       * know that to be sure.
       */
      { path: "support", element: <SupportPage /> },
      { path: "support/new", element: <NewTicketPage /> },
      { path: "support/:ticketId", element: <TicketDetailPage /> },

      /*
       * Everything that configures the organization rather than a workload now
       * lives under /settings. Team and API keys used to sit at the top level
       * beside Integrations and Billing, which meant the sidebar grouped four
       * things one way and the URLs grouped them another.
       */
      { path: "settings/integrations", element: <IntegrationsPage /> },
      { path: "settings/billing", element: <BillingPage /> },
      /*
       * Its own page rather than a panel on Billing, which is already the
       * longest in the dashboard. It is also read at a different moment: once,
       * when somebody sets up invoicing, rather than whenever they check a
       * balance.
       */
      { path: "settings/billing-details", element: <BillingDetailsPage /> },
      { path: "settings/team", element: <TeamPage /> },
      /*
       * Profile and Security share a shell, and it is a *pathless* layout
       * route: it contributes no segment, so both keep the URLs they already
       * had. Nesting them under `/settings/account/*` would have read better in
       * the address bar and cost two more redirects, the two user-menu links
       * and every bookmark anybody has — for a shape the customer sees on the
       * page either way.
       *
       * Both belong to the person signed in rather than to whichever
       * organization the switcher happens to hold, which is the whole reason
       * they group.
       */
      {
        element: <AccountLayout />,
        children: [
          { path: "settings/profile", element: <ProfilePage /> },
          { path: "settings/security", element: <SecurityPage /> },
        ],
      },
      /*
       * API keys are the organization's, not the account's — they carry a role over
       * its apps and they spend its credit — so they sit beside Team and Billing
       * rather than under Security, which the nav keeps in the user menu because a
       * password and a second factor really are personal. Integrations was in that
       * group until it was moved into the user menu by request; it is still
       * org-scoped, and its page says so.
       */
      { path: "settings/api-keys", element: <ApiKeysPage /> },
      { path: "settings/limits", element: <LimitsPage /> },

      /*
       * The old paths, kept as redirects rather than deleted. Someone has these
       * bookmarked, and an invite email that has been sitting in an inbox for a
       * week still points at /team. `replace` so the dead URL does not end up in
       * the back button.
       */
      { path: "team", element: <Navigate to="/settings/team" replace /> },
      { path: "api-keys", element: <Navigate to="/settings/api-keys" replace /> },
      { path: "settings/account", element: <Navigate to="/settings/security" replace /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
